import { AlertTriangle, X, Info } from "lucide-react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePlanAccess } from "@/hooks/usePlanAccess";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * ImpersonationBanner - Zeigt den aktiven Impersonation-Status für SuperAdmins
 * 
 * WICHTIG: Die Impersonation dient ausschließlich zum Testen von Plan-Features.
 * - SuperAdmins sehen weiterhin ihre eigenen Unternehmensdaten
 * - Nur Plan-bezogene Features (Limits, Feature-Flags) werden simuliert
 * - Kein Zugriff auf Daten der impersonierten Company (RLS bleibt aktiv)
 */
export function ImpersonationBanner() {
  const { isImpersonating, impersonatedCompany, clearImpersonation, isLoading: impersonationLoading } = useImpersonation();
  const { plan, planLabel, trial, loading: planLoading } = usePlanAccess();

  if (impersonationLoading || planLoading) return null;
  if (!isImpersonating || !impersonatedCompany) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-traffic-amber text-amber-950 px-4 py-2 shadow-elevated">
      <div className="max-w-screen-xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5" />
          <span className="font-medium">Plan-Simulation aktiv:</span>
          <span className="font-bold">{impersonatedCompany.name}</span>
          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
            {planLabel}
          </Badge>
          {trial.isActive && (
            <Badge variant="outline" className="border-amber-800 text-amber-800">
              Trial: {trial.daysRemaining} Tage
            </Badge>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 cursor-help opacity-70 hover:opacity-100" />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="text-sm">
                  <strong>Nur Plan-Features werden simuliert.</strong><br />
                  Du siehst weiterhin deine eigenen Daten. 
                  Limits, Feature-Gates und Trial-Status werden vom ausgewählten Unternehmen übernommen.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={clearImpersonation}
          className="text-amber-950 hover:bg-amber-400"
        >
          <X className="h-4 w-4 mr-1" />
          Beenden
        </Button>
      </div>
    </div>
  );
}
